/**
 * Modal de detalle de ticket — reutilizable entre /projects/$projectId
 * (kanban) y /tickets (lista agregada). Carga el detalle del issue
 * + comments + attachments y permite agregar comentario.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Download, FileText, Send, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";

const PRIORITY_LABEL: Record<string, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "Sin prioridad",
};
const PRIORITY_BADGE: Record<string, string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

interface IssueDetail {
  issue: {
    id: string;
    identifier: string | null;
    title: string;
    state: string;
    stateGroup: string;
    priority: string;
    descriptionHtml: string;
    descriptionText: string;
  };
  comments: Array<{
    id: string;
    origin: "client" | "team";
    authorName: string | null;
    bodyHtml: string;
    bodyText: string;
    createdAt: string | null;
    updatedAt: string | null;
  }>;
  attachments: Array<{
    id: string;
    filename: string;
    contentType: string;
    sizeBytes: number | null;
    createdAt: string | null;
    downloadUrl: string;
  }>;
}

export function IssueDetailModal({
  projectId,
  issueId,
  onClose,
}: {
  projectId: string;
  issueId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery<IssueDetail>({
    queryKey: ["project", projectId, "issue", issueId],
    queryFn: () => api.get(`/projects/${projectId}/issues/${issueId}`),
  });
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/issues/${issueId}/comments`, { body: body.trim() }),
    onSuccess: () => {
      setBody("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["project", projectId, "issue", issueId] });
    },
    onError: (e) => setError(e instanceof ApiError ? JSON.stringify(e.payload) : String(e)),
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-3xl">
        <div className="flex items-baseline justify-between mb-3 gap-3">
          <div className="min-w-0 flex-1">
            {detailQ.data?.issue.identifier && (
              <div className="text-xs font-mono text-base-content/55 mb-0.5">
                {detailQ.data.issue.identifier}
              </div>
            )}
            <h3 className="font-display font-semibold text-lg leading-tight">
              {detailQ.data?.issue.title ?? "…"}
            </h3>
            {detailQ.data && (
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                <span className="badge badge-sm badge-outline">{detailQ.data.issue.state}</span>
                {detailQ.data.issue.priority !== "none" && (
                  <span
                    className={`badge badge-sm ${PRIORITY_BADGE[detailQ.data.issue.priority] ?? "badge-ghost"}`}
                  >
                    {PRIORITY_LABEL[detailQ.data.issue.priority]}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </button>
        </div>

        {detailQ.isLoading && (
          <div className="py-10 grid place-items-center">
            <span className="loading loading-spinner text-primary" />
          </div>
        )}

        {detailQ.data && (
          <>
            {detailQ.data.issue.descriptionHtml ? (
              <div className="card-elevated p-4 mb-4 prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: detailQ.data.issue.descriptionHtml }} />
              </div>
            ) : (
              <div className="card-elevated p-4 mb-4 text-sm text-base-content/55 italic">
                Sin descripción.
              </div>
            )}

            {detailQ.data.attachments.length > 0 && (
              <section className="mb-4">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
                  Archivos · {detailQ.data.attachments.length}
                </h4>
                <AttachmentsGrid attachments={detailQ.data.attachments} />
              </section>
            )}

            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-primary">
                Comentarios · {detailQ.data.comments.length}
              </h4>
              {detailQ.data.comments.length === 0 ? (
                <div className="text-sm text-base-content/55 italic">
                  Sin comentarios aún. Sé el primero en escribir.
                </div>
              ) : (
                <ul className="space-y-3">
                  {detailQ.data.comments.map((c) => (
                    <li
                      key={c.id}
                      className={
                        "rounded-lg border p-3 " +
                        (c.origin === "client"
                          ? "border-primary/30 bg-primary/5"
                          : "border-base-300/60 bg-base-100")
                      }
                    >
                      <div className="flex items-baseline justify-between gap-3 mb-1">
                        <span className="text-xs font-medium inline-flex items-center gap-2">
                          {c.origin === "client" ? (
                            <span className="badge badge-primary badge-xs">
                              {c.authorName ?? "Cliente"}
                            </span>
                          ) : (
                            <span className="badge badge-xs">Equipo Axorial</span>
                          )}
                        </span>
                        <span className="text-[10px] text-base-content/55 tabular-nums">
                          {c.createdAt
                            ? new Date(c.createdAt).toLocaleString("es-MX", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: c.bodyHtml || c.bodyText }}
                      />
                    </li>
                  ))}
                </ul>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (body.trim()) add.mutate();
                }}
                className="space-y-2 pt-2 border-t border-base-300/60"
              >
                <textarea
                  className="textarea textarea-bordered w-full"
                  rows={3}
                  placeholder="Escribe un comentario para el equipo…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
                {error && <div className="text-xs text-error">{error}</div>}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={add.isPending || !body.trim()}
                    className="btn btn-primary btn-sm"
                  >
                    {add.isPending && <span className="loading loading-spinner loading-xs" />}
                    <Send className="size-4" /> Enviar
                  </button>
                </div>
              </form>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function AttachmentsGrid({
  attachments,
}: {
  attachments: Array<{
    id: string;
    filename: string;
    contentType: string;
    sizeBytes: number | null;
    downloadUrl: string;
  }>;
}) {
  const [viewer, setViewer] = useState<{ url: string; filename: string } | null>(null);
  const isImage = (ct: string) => ct.startsWith("image/");
  const isVideo = (ct: string) => ct.startsWith("video/");
  const isAudio = (ct: string) => ct.startsWith("audio/");
  const fmtSize = (n: number | null) => {
    if (n === null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <>
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {attachments.map((a) => {
          if (isImage(a.contentType)) {
            return (
              <li
                key={a.id}
                className="rounded-lg border border-base-300/60 overflow-hidden bg-base-100"
              >
                <button
                  type="button"
                  onClick={() => setViewer({ url: a.downloadUrl, filename: a.filename })}
                  className="block w-full text-left"
                >
                  <img
                    src={a.downloadUrl}
                    alt={a.filename}
                    className="w-full aspect-video object-cover hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                </button>
                <div className="px-1.5 py-1 text-[10px] text-base-content/65">
                  <div className="truncate" title={a.filename}>
                    {a.filename}
                  </div>
                  <div className="text-base-content/45">{fmtSize(a.sizeBytes)}</div>
                </div>
              </li>
            );
          }
          if (isVideo(a.contentType)) {
            return (
              <li
                key={a.id}
                className="rounded-lg border border-base-300/60 overflow-hidden bg-base-100 col-span-2"
              >
                <video
                  src={a.downloadUrl}
                  controls
                  preload="metadata"
                  className="w-full aspect-video object-contain bg-black"
                />
                <div className="px-1.5 py-1 text-[10px] text-base-content/65">
                  <div className="truncate" title={a.filename}>
                    {a.filename}
                  </div>
                  <div className="text-base-content/45">{fmtSize(a.sizeBytes)}</div>
                </div>
              </li>
            );
          }
          if (isAudio(a.contentType)) {
            return (
              <li
                key={a.id}
                className="rounded-lg border border-base-300/60 bg-base-100 p-2 col-span-2"
              >
                <div className="text-xs font-medium truncate mb-1" title={a.filename}>
                  {a.filename}
                </div>
                <audio src={a.downloadUrl} controls className="w-full" preload="metadata" />
              </li>
            );
          }
          return (
            <li
              key={a.id}
              className="rounded-lg border border-base-300/60 bg-base-100 p-3 flex flex-col gap-1"
            >
              <div className="flex items-start gap-2">
                <FileText className="size-4 text-base-content/55 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate" title={a.filename}>
                    {a.filename}
                  </div>
                  <div className="text-[10px] text-base-content/55">{fmtSize(a.sizeBytes)}</div>
                </div>
              </div>
              <a
                href={a.downloadUrl}
                download={a.filename}
                className="btn btn-ghost btn-xs justify-start gap-1 mt-1"
              >
                <Download className="size-3" /> Descargar
              </a>
            </li>
          );
        })}
      </ul>
      {viewer && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setViewer(null)}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <button
            type="button"
            onClick={() => setViewer(null)}
            className="absolute top-4 right-4 btn btn-circle btn-sm bg-base-100/20 text-white border-0"
            aria-label="Cerrar"
          >
            <X className="size-5" />
          </button>
          <img
            src={viewer.url}
            alt={viewer.filename}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
